import type { ReactNode } from 'react'

import { LoginButtons } from '@/components/Login'
import { CopyQuotesLinkButton } from '@/components/quotes/CopyQuotesLinkButton'
import { useQuoteCart } from '@/components/quotes/QuoteCartContext'
import { appHref } from '@/config/config'
import { useOrgBroker } from '@/models/broker'
import { ProductFlowNav } from '@/routes/Public/marketing/components/ProductFlowNav'

/** Split out of the route so it can read the cart whose provider wraps it. */
export function InsuranceApplicationNav({
  quotesApplicationId,
  awaitingReview,
  action,
}: {
  /** The application whose quotes are on screen — unset while the chat flow is running. */
  quotesApplicationId?: string
  /** The customer still has to confirm their information, so the share link and the cart wait. */
  awaitingReview?: boolean
  /** The page's own action on the right, in place of the quotes share link. */
  action?: ReactNode
}) {
  const { broker, isLoading: brokerLoading } = useOrgBroker()
  const cart = useQuoteCart()
  const sharedApplicationId = awaitingReview ? undefined : quotesApplicationId

  return (
    // A matched broker replaces the generic support number with the person who owns the account.
    <ProductFlowNav
      hideLogin
      accountMenu={<LoginButtons trigger="menu" />}
      containerAligned
      allowBrokerNote
      matchedBrokerPending={brokerLoading}
      // Unlinked on the quotes page: it was the only way off it, and customers landed on the dashboard mid-review.
      logoHref={quotesApplicationId ? null : appHref('/insure/insurance')}
      rightAction={
        action ??
        (sharedApplicationId ? <CopyQuotesLinkButton applicationId={sharedApplicationId} /> : undefined)
      }
      cartCount={sharedApplicationId ? cart.count : undefined}
      onCartClick={cart.open}
      matchedBroker={
        broker
          ? {
              name: broker.name,
              phone: broker.phone,
              phoneE164: broker.phoneE164,
              email: broker.email,
              avatarUrl: broker.photoUrl || undefined,
            }
          : undefined
      }
    />
  )
}
